// Ask conversations: saved on the server and shared by the household, so you
// can pick one up later, or join the one the other person is having. The
// Ask page and the voice assistant use the same one, so a question asked by
// voice on Today continues on the Ask screen.

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { AskMessage } from '../domain/types.ts';
import { api, apiUrl } from './api.ts';
import { devicePrefs } from './device.ts';
import { useCollection, useDevice } from './hooks.ts';
import { uid } from './ids.ts';
import { data } from './index.ts';
import { setServerSpeech } from './voice.ts';

export interface Turn {
  id: string;
  conv: string;
  role: 'user' | 'assistant';
  content: string;
  at: number;
  error?: boolean;
  /** Asked by voice. */
  voice?: boolean;
  /** A photo sent with the question (small JPEG data URL). */
  photo?: string;
  /** Who asked (person id), and their name. */
  by?: string;
  name?: string;
  /** Still being written. */
  streaming?: boolean;
}

// --- Which conversation this device is in ----------------------------------------------

const CURRENT = 'arlo:ask-conversation';
let current = readCurrent();
const currentListeners = new Set<() => void>();

function readCurrent(): string {
  try {
    const v = localStorage.getItem(CURRENT);
    if (v) return v;
  } catch {
    /* storage unavailable */
  }
  return uid();
}

/** Continue (or join) a conversation on this device. */
export function openConversation(id: string): void {
  current = id;
  try {
    localStorage.setItem(CURRENT, id);
  } catch {
    /* storage unavailable */
  }
  currentListeners.forEach((l) => l());
}

/** Start a fresh conversation; the old one stays in the list. */
export function newConversation(): string {
  const id = uid();
  openConversation(id);
  return id;
}

export function useCurrentConversation(): string {
  return useSyncExternalStore(
    (l) => {
      currentListeners.add(l);
      return () => currentListeners.delete(l);
    },
    () => current,
  );
}

// --- Messages: saved and shared by the server; shown here as they're written ------------

/** This device's own question and answer while they're in flight (before the saved copies arrive). */
const local = new Map<string, Turn>();
let localVersion = 0;
const localListeners = new Set<() => void>();
const touchLocal = () => {
  localVersion++;
  localListeners.forEach((l) => l());
};
const useLocalVersion = () =>
  useSyncExternalStore(
    (l) => {
      localListeners.add(l);
      return () => localListeners.delete(l);
    },
    () => localVersion,
  );

/** The saved copy, unless this device's live copy is further along. */
function merge(saved: AskMessage[], mine: Turn[]): Turn[] {
  const byId = new Map<string, Turn>(saved.map((m) => [m.id, m]));
  for (const t of mine) {
    const s = byId.get(t.id);
    if (!s || (t.error && !s.content) || (s.streaming && t.content.length > s.content.length)) byId.set(t.id, t);
  }
  return [...byId.values()].sort((a, b) => a.at - b.at || (a.role === 'user' ? -1 : 1));
}

/** One conversation's messages, oldest first — live, whoever is asking. */
export function useConversation(conv: string): Turn[] {
  const saved = useCollection('askMessages');
  const v = useLocalVersion();
  return useMemo(
    () =>
      merge(
        saved.filter((m) => m.conv === conv),
        [...local.values()].filter((t) => t.conv === conv),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saved, conv, v],
  );
}

export interface ConversationSummary {
  id: string;
  /** The first question. */
  title: string;
  startedAt: number;
  updatedAt: number;
  /** Everyone who's asked something in it. */
  people: string[];
  /** Who asked last. */
  lastBy?: string;
  lastName?: string;
  questions: number;
  /** An answer is being written right now. */
  live: boolean;
}

/** Every conversation, most recent first. */
export function useConversations(): ConversationSummary[] {
  const saved = useCollection('askMessages');
  return useMemo(() => {
    const groups = new Map<string, AskMessage[]>();
    for (const m of saved) groups.set(m.conv, [...(groups.get(m.conv) ?? []), m]);
    const now = Date.now();
    return [...groups.entries()]
      .map(([id, list]) => {
        const sorted = [...list].sort((a, b) => a.at - b.at);
        const asked = sorted.filter((m) => m.role === 'user');
        const last = asked[asked.length - 1];
        return {
          id,
          title: asked[0]?.content.slice(0, 80) ?? 'Conversation',
          startedAt: sorted[0].at,
          updatedAt: sorted[sorted.length - 1].at,
          people: [...new Set(asked.map((m) => m.name).filter((n): n is string => !!n))],
          lastBy: last?.by,
          lastName: last?.name,
          questions: asked.length,
          live: sorted.some((m) => m.streaming && now - m.at < 3 * 60_000),
        };
      })
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }, [saved]);
}

/** Remove a conversation for everyone. */
export function deleteConversation(conv: string): void {
  const { store } = data();
  for (const m of store.list('askMessages')) if (m.conv === conv) store.remove('askMessages', m.id);
  for (const [id, t] of local) if (t.conv === conv) local.delete(id);
  touchLocal();
  if (current === conv) newConversation();
}

let busy = false;
export const askBusy = () => busy;

/** A spoken question after a long gap starts afresh, rather than continuing this morning's chat. */
const STALE = 2 * 60 * 60_000;

/**
 * Ask a question in a conversation (this device's current one by default).
 * The answer streams in here; the server saves both, so the other phone
 * sees them and either of you can continue. `onText` gets each text delta
 * (the voice assistant speaks sentences as they complete). Resolves with the
 * full answer, or throws with a friendly message.
 */
/** A photo ready to ask about: `image` for the AI, `thumb` for the chat. */
export interface AskPhoto {
  image: string;
  thumb: string;
}

/** The question asked with just a photo (the server says the same). */
export const PHOTO_QUESTION = 'What do you make of this photo?';

/** Draw a photo no bigger than `edge` on its longest side, the right way up. */
async function scaled(file: Blob, edge: number): Promise<HTMLCanvasElement> {
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
  const k = Math.min(1, edge / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(bmp.width * k));
  c.height = Math.max(1, Math.round(bmp.height * k));
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close();
  return c;
}

/**
 * A photo from the camera or gallery, made ready to ask about: big enough to
 * read a feeding table on a bag (1600 px), small enough to send on a weak
 * signal — and a small copy for the chat, which every phone syncs.
 */
export async function preparePhoto(file: Blob): Promise<AskPhoto> {
  const big = await scaled(file, 1600);
  let image = big.toDataURL('image/jpeg', 0.85);
  if (image.length > 6_000_000) image = big.toDataURL('image/jpeg', 0.6);
  const small = await scaled(file, 720);
  let thumb = small.toDataURL('image/jpeg', 0.72);
  if (thumb.length > 150_000) thumb = small.toDataURL('image/jpeg', 0.5);
  return { image, thumb };
}

export async function ask(question: string, opts: { voice?: boolean; onText?: (delta: string) => void; signal?: AbortSignal; conversation?: string; photo?: AskPhoto } = {}): Promise<string> {
  const q = question.trim() || (opts.photo ? PHOTO_QUESTION : '');
  if (!q) return '';
  let conv = opts.conversation ?? current;
  if (!opts.conversation && opts.voice) {
    const last = Math.max(0, ...data().store.list('askMessages').filter((m) => m.conv === conv).map((m) => m.at));
    if (last && Date.now() - last > STALE) conv = newConversation();
  }
  if (conv !== current) openConversation(conv);
  busy = true;
  const d = devicePrefs();
  const by = d.guest ? undefined : d.personId;
  const name = data().store.get('settings', 'household')?.people.find((p) => p.id === by)?.name;
  const at = Date.now();
  const qid = uid();
  const aid = uid();
  local.set(qid, { id: qid, conv, role: 'user', content: q, at, by, name, voice: opts.voice || undefined, photo: opts.photo?.thumb });
  local.set(aid, { id: aid, conv, role: 'assistant', content: '', at: at + 1, streaming: true });
  touchLocal();
  const update = (patch: Partial<Turn>) => {
    const a = local.get(aid);
    if (a) local.set(aid, { ...a, ...patch });
    touchLocal();
  };
  let answer = '';
  try {
    const res = await fetch(apiUrl('/api/ask'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(d.key ? { Authorization: `Bearer ${d.key}` } : {}) },
      body: JSON.stringify({ question: q, conversation: conv, questionId: qid, answerId: aid, by, mode: opts.voice ? 'voice' : undefined, image: opts.photo?.image, thumb: opts.photo?.thumb }),
      signal: opts.signal,
    });
    if (!res.ok || !res.body) {
      const b = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(b.error ?? `Error ${res.status}`);
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const ev = /^event: (.*)$/m.exec(chunk)?.[1];
        const data = /^data: (.*)$/m.exec(chunk)?.[1];
        if (!ev || data === undefined) continue;
        const payload = JSON.parse(data);
        if (ev === 'text') {
          answer += payload;
          update({ content: answer });
          opts.onText?.(payload);
        }
        if (ev === 'error') throw new Error(payload.message);
      }
    }
    update({ streaming: false });
    return answer;
  } catch (err) {
    const aborted = (err as Error).name === 'AbortError';
    const message = aborted ? answer || 'Stopped.' : (err as Error).message || 'Something went wrong.';
    update({ content: aborted ? message : answer ? `${answer}\n\n(${message})` : message, error: !aborted && !answer, streaming: false });
    throw new Error(message);
  } finally {
    busy = false;
  }
}

export interface AskInfo {
  enabled: boolean;
  voice: 'neural' | 'device';
  voices: { id: string; label: string }[];
  /** The server can transcribe speech (for browsers that can't). */
  stt: boolean;
}

let info: AskInfo | undefined;

/** Is AI switched on, and are neural voices available? (null while checking) */
export function useAskInfo(): AskInfo | null {
  const d = useDevice();
  const [state, setState] = useState<AskInfo | null>(info ?? null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!d.key) return setState({ enabled: false, voice: 'device', voices: [], stt: false });
    api<AskInfo>('/api/ask')
      .then((r) => {
        info = { enabled: !!r.enabled, voice: r.voice === 'neural' ? 'neural' : 'device', voices: r.voices ?? [], stt: !!r.stt };
        setServerSpeech(info.stt);
        setState(info);
      })
      // A weak signal isn't "AI is off": stay unknown (asking still works) and check again shortly.
      .catch(() => setTimeout(() => setRetry((n) => n + 1), 15_000));
  }, [d.key, d.serverUrl, retry]);
  return state;
}
